import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import "pdfjs-dist/web/pdf_viewer.css";
import { Button, Box, Typography, makeStyles } from "@material-ui/core";
import { openDB } from "idb"; // Import IndexedDB wrapper
import { Skeleton } from "@mui/material";
import { toast } from "react-toastify";

// Configure worker
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const useStyles = makeStyles((theme) => ({
  viewerContainer: {
    position: "relative",
    textAlign: "center",
    marginBottom: theme.spacing(3),
  },
  canvas: {
    border: "1px solid #ccc",
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    maxWidth: "100%",
    height: "auto",
  },
  navButtons: {
    marginTop: theme.spacing(2),
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  pageInfo: {
    marginTop: theme.spacing(1),
  },
  skeletonContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "800px",
  },
}));

const EXPIRY_DAYS = 30;

const CustomPDFViewer = ({ url, bookId, onPageChange = () => {} }) => {
  const classes = useStyles();
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const canvasRef = useRef(null);
  const pdfRef = useRef(null);
  const renderTaskRef = useRef(null);

  // IndexedDB instance
  const dbPromise = openDB("pdfViewerDB", 1, {
    upgrade(db) {
      const store = db.createObjectStore("bookPages", {
        keyPath: "bookId",
      });
      store.createIndex("bookId", "bookId");
    },
  });

  const hasExpired = (savedTimestamp) => {
    const now = new Date().getTime();
    return now - savedTimestamp > EXPIRY_DAYS * 24 * 60 * 60 * 1000; // Days to milliseconds
  };

  // Store data in IndexedDB with expiration
  const saveCurrentPage = async (page) => {
    const timestamp = new Date().getTime();
    const db = await dbPromise;
    await db.put("bookPages", {
      bookId,
      pageNumber: page,
      timestamp,
    });
  };

  // Load data from IndexedDB and clean expired data
  const loadPageFromDB = async () => {
    const db = await dbPromise;
    const savedData = await db.get("bookPages", bookId);

    if (savedData && !hasExpired(savedData.timestamp)) {
      setPageNumber(savedData.pageNumber);
      onPageChange(savedData.pageNumber);
    } else {
      // Clean up expired data
      await db.delete("bookPages", bookId);
    }
  };

  useEffect(() => {
    if (!bookId) {
      console.error("Invalid bookId:", bookId);
      return;
    }

    loadPageFromDB(); // Load page from IndexedDB on component mount

    const loadingTask = pdfjsLib.getDocument(url);
    loadingTask.promise
      .then((pdf) => {
        pdfRef.current = pdf;
        setNumPages(pdf.numPages);
        renderPage(pdf, pageNumber);
      })
      .catch((error) => {
        console.error("PDF loading error:", error);
        toast.error(`PDF loading error:", ${error}`);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
    };
  }, [url, bookId]);

  const renderPage = (pdf, pageNum) => {
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
    }

    pdf
      .getPage(pageNum)
      .then((page) => {
        const viewport = page.getViewport({
          scale: canvasRef.current.parentNode.clientWidth / page.view[2],
        });

        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport,
        };

        renderTaskRef.current = page.render(renderContext);
        renderTaskRef.current.promise
          .then(() => {
            // Scroll back to top of the page after rendering the new page
            window.scrollTo(0, 0);
          })
          .catch((error) => {
            if (!(error instanceof pdfjsLib.RenderingCancelledException)) {
              console.error("Render error:", error);
            }
          });
      })
      .catch((error) => console.error("Page rendering error:", error));
  };

  // Ensure renderPage is called when pageNumber changes
  useEffect(() => {
    if (pdfRef.current) {
      renderPage(pdfRef.current, pageNumber);
    }
  }, [pageNumber]);

  const goToNextPage = () => {
    if (pageNumber < numPages) {
      setPageNumber((prevPageNumber) => {
        const newPageNumber = prevPageNumber + 1;
        saveCurrentPage(newPageNumber);
        onPageChange(newPageNumber);
        return newPageNumber;
      });
    }
  };

  const goToPrevPage = () => {
    if (pageNumber > 1) {
      setPageNumber((prevPageNumber) => {
        const newPageNumber = prevPageNumber - 1;
        saveCurrentPage(newPageNumber);
        onPageChange(newPageNumber);
        return newPageNumber;
      });
    }
  };

  return (
    <Box className={classes.viewerContainer}>
      {loading ? (
        <Box className={classes.skeletonContainer}>
          <Skeleton variant="rect" width={600} height={800} animation="wave" />
        </Box>
      ) : (
        <canvas ref={canvasRef} className={classes.canvas}></canvas>
      )}

      <Box className={classes.navButtons}>
        <Button
          variant="contained"
          color="primary"
          onClick={goToPrevPage}
          disabled={pageNumber <= 1}
        >
          Previous
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={goToNextPage}
          disabled={pageNumber >= numPages}
        >
          Next
        </Button>
      </Box>

      <Typography variant="body1" className={classes.pageInfo}>
        Page {pageNumber} of {numPages}
      </Typography>
    </Box>
  );
};

export default CustomPDFViewer;
